<script setup lang="ts">
import { TransitionRoot } from "@headlessui/vue";
import { computed, nextTick } from "vue";
import { RouterView, useRouter } from "vue-router";

import AppMetaManager from "~/components/dumb/AppMetaManager.vue";
import ContextMenu from "~/components/dumb/ContextMenu.vue";
import FeedbackTooltip from "~/components/dumb/FeedbackTooltip.vue";
import Loading from "~/components/dumb/Loading.vue";
import TaskPreviewTooltip from "~/components/dumb/TaskPreviewTooltip.vue";
import MobileNavigationSwipe from "~/components/MobileNavigationSwipe.vue";
import NativeRedirectOverlay from "~/components/NativeRedirectOverlay.vue";
import { notify } from "~/components/notifications";
import NotificationList from "~/components/notifications/NotificationList.vue";
import { BACKUP_FONTS, colorsByTheme } from "~/constants/style";
import { getQueryParam } from "~/router/common";
import { useAppStore, useEnvironmentStore, usePageStore } from "~/stores";
import { when } from "~/utils/wait";

const router = useRouter();
const appStore = useAppStore();
const environmentStore = useEnvironmentStore();
const pageStore = usePageStore();

const showOfflineBanner = computed(() => !pageStore.isOnline && !environmentStore.isLocal);
const colors = computed(() => colorsByTheme[pageStore.theme]);
const transparentBg = pageStore.isDesktopApp && pageStore.isMac && pageStore.isVersionGreaterOrEqual("1.0.2");

if (environmentStore.isProd) {
  // eslint-disable-next-line no-console
  console.log(
    "%c🎯 Dart is hiring!%c\nJoin us to build the only truly AI-native project management tool.\n%chttps://dartai.com/careers",
    "color: #5420dd; font-size: 16px; font-weight: bold;",
    "font-size: 14px;",
    "color: #1a0dab; font-size: 14px; text-decoration: underline;"
  );
}

// TODO popper remove this once we remove floating-vue
document.addEventListener("scroll", () => {
  if ((!pageStore.isMobile || appStore.taskOpenInDetail) && document.documentElement.scrollTop !== 0) {
    document.documentElement.scrollTop = 0;
  }
});

if (pageStore.isMobile) {
  document.documentElement.classList.add("blink-input");
  document.documentElement.style.background = colors.value.bgStd;

  // Resize page when keyboard is shown on mobile
  window.visualViewport?.addEventListener("resize", () => {
    document.documentElement.style.height = `${window.visualViewport?.height}px`;

    const modalWrapper = document.querySelector(".dart-modal-wrapper") as HTMLElement;
    if (modalWrapper) {
      modalWrapper.style.height = `${window.visualViewport?.height}px`;
    }
  });
}

when(
  computed(() => router.currentRoute.value.fullPath !== "/"),
  () => {
    const redirected = !!getQueryParam("redirected");
    const nextRedirected = !!new URLSearchParams(
      decodeURIComponent(decodeURIComponent(getQueryParam("next") ?? ""))
    ).get("redirected");
    if (!redirected && !nextRedirected) {
      return;
    }

    const query = { ...router.currentRoute.value.query };
    if (redirected) {
      delete query.redirected;
    }
    if (nextRedirected) {
      delete query.next;
    }
    nextTick(() => router.replace({ query }));

    notify({
      message: "You were brought to the app from the website so you could get right to work",
      actions: [
        {
          label: "Go back",
          onClick: () => {},
          component: "a",
          componentArgs: {
            href: "https://dartai.com/?nr=1",
          },
        },
      ],
    });
  }
);
</script>

<template>
  <div
    class="flex h-full flex-col subpixel-antialiased bg-std selection:bg-indigo-200 dark:selection:bg-indigo-900"
    :class="pageStore.isIos && pageStore.isMobileApp && 'pb-[18px]'"
    :style="{ '--background': colors.bgStd, '--highlight': colors.highlight }">
    <TransitionRoot
      :show="!(pageStore.userDataLoaded && pageStore.pageLoaded)"
      class="absolute left-0 top-0 z-50 h-screen w-screen"
      leave="ease-in duration-300"
      leave-from="opacity-100"
      leave-to="opacity-0">
      <Loading :show-timing="pageStore.showDebugInfo" :transparent-bg="transparentBg" />
    </TransitionRoot>
    <RouterView v-if="pageStore.userDataLoaded" :class="showOfflineBanner && 'min-h-0'" />
    <div
      v-if="showOfflineBanner"
      class="flex h-7 w-full items-center justify-center gap-6 bg-primary-base px-4 text-sm text-oncolor">
      <span class="font-bold uppercase">Offline</span>
      Changes will sync when Dart reconnects
    </div>
    <AppMetaManager />
    <ContextMenu />
    <FeedbackTooltip />
    <NotificationList />
    <TaskPreviewTooltip />
    <MobileNavigationSwipe v-if="pageStore.isMobile" />
    <!-- Native overlay should be last so it's on top -->
    <NativeRedirectOverlay />
  </div>
</template>

<style>
/* Scrollbar styles */
::-webkit-scrollbar {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  border: 4px solid var(--background);
  background: color-mix(in srgb, var(--highlight) 80%, transparent);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover,
::-webkit-scrollbar-thumb:active {
  background: var(--highlight);
}
::-webkit-scrollbar-corner {
  background: transparent;
}

/* OverlayScrollbars styles */
.os-scrollbar {
  --os-size: 15px;
  --os-padding-perpendicular: 4px;
  --os-padding-axis: 0;
  --os-track-border-radius: 0;
  --os-handle-border-radius: 3.5px;
  --os-handle-min-size: 30px;
  --os-handle-max-size: none;
  --os-handle-perpendicular-size: 7px;
  --os-handle-interactive-area-offset: 0;
}
.os-scrollbar-vertical {
  right: 0;
  left: unset;
}
.os-scrollbar-handle {
  background: color-mix(in srgb, var(--highlight) 80%, transparent);
}
.os-scrollbar-handle:hover,
.os-scrollbar-handle:active {
  background: var(--highlight);
}

/* Textarea styles */
textarea.resize-none {
  overflow: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}
textarea.resize-none::-webkit-scrollbar {
  display: none;
}

#commandbar-wrapper * {
  font-family: "Inter var", v-bind("BACKUP_FONTS") !important;
}
/* Remove commandbar branding */
.commandbar-content-container > div:last-child {
  display: none;
}
#commandbar-menu__wrapper {
  border-radius: 0 0 10px 10px;
}
/* Dark mode colors */
.dark #commandbar-wrapper {
  --content-primary: red;
}
.dark #commandbar__control_wrapper,
.dark .commandbar-content-container {
  background-color: #1e1e21;
  border-color: #27272a;
}
.dark
  #commandbar-menu__wrapper
  > div:first-child
  > div:first-child
  > div:first-child
  > div:first-child
  > div:first-child {
  background-color: #1e1e21 !important;
}
.dark
  #commandbar-menu__wrapper
  > div:first-child
  > div:first-child
  > div:first-child
  > div:first-child
  > div:first-child
  > div:first-child
  > div:first-child,
.dark #commandbar_menu_list div[aria-label^="category"] div:first-child {
  background-color: unset !important;
}
</style>
